import { Component, Input, OnInit } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { ImageUrlService } from 'src/app/services/image-url.service';
import { ApiService } from 'src/app/services/api.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { CategoryFormDialogComponent } from './category-form-dialog/category-form-dialog.component';

@Component({
  selector: 'app-category-management',
  templateUrl: './category-management.component.html',
  styleUrls: ['./category-management.component.scss']
})
export class CategoryManagementComponent implements OnInit {

  @Input() storeId: string = '';

  categories: any[] = [];
  loading: boolean = false;
  searchQuery: string = '';
  sortBy: string = 'name';

  constructor(
    private api: ApiService,
    private ecommerceService: EcommerceService,
    private imageUrlService: ImageUrlService,
    private dialogService: NbDialogService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (this.storeId) {
      this.loadCategories();
    }
  }

  loadCategories(): void {
    this.loading = true;
    this.ecommerceService.getStoreCategories(this.storeId, { adminView: 'true' }).subscribe(
      (res: any) => {
        if (res.response && res.response.success && res.response.data) {
          this.categories = res.response.data.categories || [];
          this.sortCategories();
        } else if (res.code === 200 && res.data) {
          this.categories = res.data.categories || [];
          this.sortCategories();
        } else {
          this.categories = [];
        }
        this.loading = false;
      },
      (error: any) => {
        this.toastr.error('Failed to load categories');
        this.loading = false;
      }
    );
  }

  sortCategories(): void {
    this.categories.sort((a, b) => {
      switch (this.sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'displayOrder':
          return (a.displayOrder || 0) - (b.displayOrder || 0);
        case 'createdAt':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'productsCount':
          return (b.productsCount || 0) - (a.productsCount || 0);
        default:
          return 0;
      }
    });
  }

  onSearch(): void {
    if (this.searchQuery.trim()) {
      this.categories = this.categories.filter(category =>
        category.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        (category.description && category.description.toLowerCase().includes(this.searchQuery.toLowerCase()))
      );
    } else {
      this.loadCategories();
    }
  }

  onSortChange(): void {
    this.sortCategories();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.loadCategories();
  }

  openAddCategoryDialog(): void {
    this.dialogService.open(CategoryFormDialogComponent, {
      context: { storeId: this.storeId, category: null },
      hasBackdrop: true,
      closeOnBackdropClick: false
    }).onClose.subscribe((result: any) => {
      if (result?.saved) this.loadCategories();
    });
  }

  editCategory(category: any): void {
    this.dialogService.open(CategoryFormDialogComponent, {
      context: { storeId: this.storeId, category: category },
      hasBackdrop: true,
      closeOnBackdropClick: false
    }).onClose.subscribe((result: any) => {
      if (result?.saved) this.loadCategories();
    });
  }

  deleteCategory(categoryId: string): void {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This will permanently delete the category and may affect associated products',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDeleteCategory(categoryId);
      }
    });
  }

  performDeleteCategory(categoryId: string): void {
    this.spinner.show();
    this.ecommerceService.deleteCategory(this.storeId, categoryId).subscribe(
      (res: any) => {
        if ((res.response && res.response.success) || res.code === 200) {
          this.toastr.success('Category deleted successfully');
          this.loadCategories();
        }
        this.spinner.hide();
      },
      (error: any) => {
        this.toastr.error('Failed to delete category');
        this.spinner.hide();
      }
    );
  }

  trackByCategoryId(index: number, category: any): string {
    return category._id;
  }

  getImageUrl(url: string): string {
    return this.imageUrlService.fixImageUrl(url);
  }

  onImageError(event: any): void {
    const imgElement = event.target as HTMLImageElement;
    if (imgElement) {
      imgElement.style.display = 'none';
    }
  }

  toggleManagement(category: any): void {
    const newValue = !category.managementEnabled;
    this.api.putRequest(
      `products/store/${this.storeId}/category/${category._id}`,
      { managementEnabled: newValue }
    ).subscribe({
      next: (res: any) => {
        category.managementEnabled = newValue;
      },
      error: (err: any) => {
        category.managementEnabled = !newValue;
      }
    });
  }
}
